import { useEffect, useState, useCallback } from "react";
import { Users as UsersIcon, ShieldCheck, UserCog, Crown, Wrench, Trash2, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const ROLE_META = {
  customer: { label: "Customer", icon: UsersIcon, badge: "bg-slate-100 text-slate-700" },
  technician: { label: "Technician", icon: Wrench, badge: "bg-cyan-100 text-cyan-800" },
  admin: { label: "Admin", icon: ShieldCheck, badge: "bg-blue-100 text-blue-800" },
  owner: { label: "Owner", icon: Crown, badge: "bg-amber-100 text-amber-800" },
};

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // user object pending delete
  const [deleting, setDeleting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const blankForm = { email: "", password: "", name: "", phone: "", role: "owner" };
  const [form, setForm] = useState(blankForm);

  const load = useCallback(async () => {
    try { const r = await api.get("/users"); setUsers(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load users"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChangeRole = async (userId, role) => {
    setBusyId(userId);
    try {
      const r = await api.patch(`/users/${userId}/role`, { role });
      setUsers((u) => u.map((x) => (x.user_id === userId ? r.data : x)));
      toast.success(`Role updated to ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusyId(null); }
  };

  const onConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmDelete.user_id}`);
      setUsers((u) => u.filter((x) => x.user_id !== confirmDelete.user_id));
      toast.success(`Deleted ${confirmDelete.email}`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const isOwner = me?.role === "owner";

  const onCreate = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Email, password and name are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        role: form.role,
      });
      setUsers((u) => [r.data, ...u]);
      toast.success(`Created ${r.data.email} (${r.data.role})`);
      setForm(blankForm);
      setCreateOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">{users.length} total — newest first</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden text-xs text-slate-500 md:block">
            {isOwner ? "You can promote anyone, including to Owner." : "Admins can change Customer ↔ Admin. Only an Owner can grant Owner."}
          </div>
          <Button onClick={() => { setForm(blankForm); setCreateOpen(true); }} data-testid="users-create-button" className="gap-2">
            <UserPlus className="h-4 w-4" /> Create user
          </Button>
        </div>
      </div>

      <div className="mt-6 dash-surface overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Sign-in</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.customer;
              const isSelf = u.user_id === me?.user_id;
              const isTargetOwner = u.role === "owner";
              // Disable: self-edit, or non-owner trying to edit an owner row
              const disabled = isSelf || (!isOwner && isTargetOwner) || busyId === u.user_id;
              // Delete: never delete self; only owner can delete owners
              const canDelete = !isSelf && !(isTargetOwner && !isOwner);
              return (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </span>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{u.name || "—"}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">(you)</span>}</div>
                        <div className="text-[11px] text-slate-400">{u.user_id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{u.email}</TableCell>
                  <TableCell className="text-xs text-slate-500"><code className="font-mono">{u.auth_provider}</code></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${meta.badge}`}>
                        <meta.icon className="h-3 w-3" /> {meta.label}
                      </span>
                      <Select value={u.role} disabled={disabled} onValueChange={(v) => onChangeRole(u.user_id, v)}>
                        <SelectTrigger className="w-32" data-testid={`user-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="technician">Technician</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          {/* Owner option only available when current user is owner */}
                          {isOwner && <SelectItem value="owner">Owner</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(u)}
                      disabled={!canDelete}
                      title={isSelf ? "You cannot delete yourself" : (!canDelete ? "Only an owner can delete an owner" : "Delete user")}
                      className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                      data-testid={`user-delete-button-${u.user_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
            {users.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500"><UserCog className="mx-auto mb-2 h-6 w-6 text-slate-400" /> No users yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="mt-3 text-xs text-slate-500">
        <strong>Customer</strong> can shop and track their own orders/quotes. <strong>Technician</strong> sees only their assigned jobs (orders) and can update status + GPS. <strong>Admin</strong> manages products and quotes. <strong>Owner</strong> can also edit site settings, payment options, and change user roles.
      </p>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open && !deleting) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="user-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong className="text-slate-900">{confirmDelete?.name || confirmDelete?.email}</strong>
              {confirmDelete?.email ? <> ({confirmDelete.email})</> : null} from the system. Their active sessions will be revoked. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="user-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirmDelete(); }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500"
              data-testid="user-delete-confirm-button"
            >
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</> : <><Trash2 className="mr-2 h-4 w-4" /> Delete user</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent data-testid="users-create-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new user</DialogTitle>
            <DialogDescription>
              They’ll be able to sign in immediately with the email and password you set.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreate} className="grid gap-3">
            <div>
              <Label htmlFor="cu-name">Full name</Label>
              <Input id="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="users-create-name" />
            </div>
            <div>
              <Label htmlFor="cu-email">Email</Label>
              <Input id="cu-email" type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" data-testid="users-create-email" />
            </div>
            <div>
              <Label htmlFor="cu-password">Temporary password</Label>
              <Input id="cu-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" data-testid="users-create-password" />
              <p className="mt-1 text-[11px] text-slate-500">Share this with them privately — they can change it after their first sign-in.</p>
            </div>
            <div>
              <Label htmlFor="cu-phone">Phone <span className="text-slate-400">(optional)</span></Label>
              <Input id="cu-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(423) 555-0100" data-testid="users-create-phone" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="users-create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {isOwner && <SelectItem value="owner">Owner</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} data-testid="users-create-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="users-create-submit">
                {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</> : <><UserPlus className="mr-2 h-4 w-4" /> Create user</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
